import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
// React 17+ JSX runtime; no explicit React import needed
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { createBaseChartConfig, type ChartData } from './chartConfigUtils';

interface StandardChartConfig {
    readonly chartType: 'column' | 'line' | 'spline';
    readonly height?: number;
    readonly yAxisTitleKey: string;
    readonly yAxisTitleFallback: string;
    readonly yAxisColor: string;
    readonly seriesNameKey: string;
    readonly seriesNameFallback: string;
    readonly seriesColor: string;
    readonly valueSuffix?: string;
}

interface StandardChartProps {
    readonly data: readonly ChartData[];
    readonly loading?: boolean;
    readonly referenceValue?: number;
    readonly config: StandardChartConfig;
}

function StandardChart({ data, loading = false, referenceValue, config }: StandardChartProps) {
    const { t } = useTranslation();

    const options: Highcharts.Options = createBaseChartConfig(
        data,
        {
            chartType: config.chartType,
            height: config.height,
            yAxisTitle: t(config.yAxisTitleKey, config.yAxisTitleFallback),
            yAxisColor: config.yAxisColor,
            seriesName: t(config.seriesNameKey, config.seriesNameFallback),
            seriesColor: config.seriesColor,
            valueSuffix: config.valueSuffix,
            referenceValue,
        },
        t,
    );

    // Show placeholder if loading or no data
    if (loading || data.length === 0) {
        return (
            <ChartPlaceholder
                loading={loading}
                hasData={data.length > 0}
                height={config.height}
            />
        );
    }

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default StandardChart;
